import { JobQueue, JobQueueService, JobType } from "@app/jobs";
import { FlyoutFooter } from "@app/shared/Flyouts";
import * as React from "react";
import { JSX } from "react";
import { toast, ToastOptions } from "react-toastify";
import { Resource } from "@app/resources";
import ProjectService from "@app/projects/projectService";
import SingleSelectNestedDropdown from "@app/shared/checkbox-dropdown/SingleSelectNestedDropdown";
import { NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { Client } from "@app/client/client";

interface TestJobQueueMappingsProps {
  clients?: Client[];
  jobTypes: { [key: string]: JobType };
  countyItems: NestingParentDropdownItem[];
}

const TestJobQueueMappingsFlyout = (props: TestJobQueueMappingsProps): JSX.Element => {
  const jobQueueService = new JobQueueService();
  const projectService = new ProjectService();

  const [clients] = React.useState<Client[]>(props.clients);
  const [jobTypes] = React.useState<{ [key: string]: JobType }>(props.jobTypes);
  const [projects, setProjects] = React.useState<Resource[]>([]);

  const [clientOptions, setClientOptions] = React.useState<JSX.Element[]>([]);
  const [jobTypeOptions, setJobTypeOptions] = React.useState<JSX.Element[]>([]);
  const [projectOptions, setProjectOptions] = React.useState<JSX.Element[]>([]);

  const [client, setClient] = React.useState<Client>(null);
  const [jobQueue, setJobQueue] = React.useState<JobQueue>(null);
  const [jobType, setJobType] = React.useState<JobType>(null);
  const [project, setProject] = React.useState<Resource>(null);
  const [countySSA, setCountySSA] = React.useState<string>("");
  const [selectedCounty, setSelectedCounty] = React.useState(null);
  const [testEnabled, setTestEnabled] = React.useState<boolean>(false);

  React.useEffect(() => {
    const sortedJobTypes = Object.keys(jobTypes).sort((jt1, jt2) => jt1.localeCompare(jt2));
    const selectedJobType = sortedJobTypes.length > 0 ? jobTypes[sortedJobTypes[0]] : null;
    const localJobTypeOptions: JSX.Element[] = [];

    sortedJobTypes.forEach(key => {
      const option = (
        <option key={key} value={key}>{jobTypes[key]}</option>
      );
      localJobTypeOptions.push(option);
    });
    setJobTypeOptions(localJobTypeOptions);
    setJobType(selectedJobType);

    const localClientOptions: JSX.Element[] = [];
    const selectedClient = clients.length > 0 ? clients[0] : null;
    (clients && clients.forEach(client => {
      const option = (
        <option key={client.clientId} value={client.clientId}>{client.name}</option>
      );
      localClientOptions.push(option);
    }));
    setClientOptions(localClientOptions);
    setClient(selectedClient);
  }, [])

  React.useEffect(() => {
    setTestEnabled(!!jobType && !!client && !!project);
  }, [jobType, client, project, countySSA])

  React.useEffect(() => {
    const getProjects = async () => {
      const options: JSX.Element[] = [];
      if (!!client) {
        const projects = await projectService.getClientProjectResources(client.clientId);
        setProjects(projects);
        !!projects && projects.forEach(project => {
          const option = (
            <option key={project.resourceId} value={project.resourceId}>{project.name}</option>
          );
          options.push(option);
        });
      }
      setProjectOptions(options);
      setProject(null);
    };
    getProjects();
  }, [client]);

  const changeClientSelection = (key: number) => {
    const selectedOption: Client = !!key
      ? clients.find((client: Client) => client.clientId === key)
      : null;
    setClient(selectedOption);
  }

  const changeJobTypeSelection = (key: string) => {
    const selectedOption: JobType = !!key
      ? jobTypes[key]
      : null;
    setJobType(selectedOption);
  }

  const changeProjectSelection = (key: number) => {
    const selectedOption: Resource = !!key
      ? projects.find((project: Resource) => project.resourceId === key)
      : null;
    setProject(selectedOption);
  }

  const testMapping = async () => {
    try {
      const jobQueue = await jobQueueService.getJobQueue(jobType, client.clientId, project.resourceId, countySSA);
      setJobQueue(jobQueue);
    }
    catch {
      const toastOptions: ToastOptions = {
        closeOnClick: true,
        containerId: "z-index-9999",
      };
      toast.error("Test Job Queue Map Failed", toastOptions);
    }
  }

  return (
    <>
      <div className="col">
        <div className="row mb5">
          <div className="col-4 label-s">Last Tested Queue:</div>
          <div className="col">{jobQueue ? `${jobQueue.name}: ${jobQueue.id}` : '(not yet tested)'}</div>
        </div>
        <div className="row mb5">
          <div className="col">
            <QESSelect
              allowEmpty={false}
              label="Job Type"
              onChange={(e) => changeJobTypeSelection(e.target.value)}
              placeholder="Select Job Type"
              testId="test-job-queue-map-flyout_jobType">
              {jobTypeOptions}
            </QESSelect>
            <QESSelect
              allowEmpty={false}
              label="Client"
              onChange={(e) => changeClientSelection(+e.target.value)}
              placeholder="Select Client"
              testId="test-job-queue-map-flyout_client">
              {clientOptions}
            </QESSelect>
            <QESSelect
              allowEmpty={true}
              label="Project"
              onChange={(e) => changeProjectSelection(+e.target.value)}
              placeholder="Select Project"
              testId="test-job-queue-map-flyout_project">
              {projectOptions}
            </QESSelect>
            <SingleSelectNestedDropdown
              containerClassName="col-12 p-0"
              items={props.countyItems}
              label="County"
              onSelectionChanged={(item) => {
                setSelectedCounty(item);
                if (!item) {
                  setCountySSA("");
                } else {
                  setCountySSA(item.key);
                }
              }}
              selectedItem={selectedCounty}
              testId="job-queue-map-filters__county"
            />
          </div>
        </div>
      </div>
      <FlyoutFooter
        submitButton={<QESButton qesTestId="test-job-queue-map-flyout_test" disabled={!testEnabled} qesType="primary" className="mb-3 me-2" onClick={testMapping}>Test</QESButton>}
      />
    </>
  );
}

export default TestJobQueueMappingsFlyout;